import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRBAC } from '../contexts/RBACContext';
import { TenantLevel, UserRole } from '../types';

interface ProtectedRouteProps {
  requiredRoles?: UserRole[];
  requiredTenantLevels?: TenantLevel[];
  requiredPermission?: string;
  requiredFeature?: string;
}

// Protected route that requires authentication
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requiredRoles = [],
  requiredTenantLevels = [],
  requiredPermission,
  requiredFeature
}) => {
  const { user, isAuthenticated, isInitializing } = useAuth();
  const { hasPermission, canAccess, loading: rbacLoading, rbacData } = useRBAC();
  
  // Route protection check
  
  // Show loading state for auth loading
  if (isInitializing) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  // If RBAC is required (permission or feature check), wait for RBAC data to load
  // This prevents false negatives when rbacData is null during initial load
  if ((requiredPermission || requiredFeature) && (rbacLoading || !rbacData)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Check RBAC permission if specified (only after RBAC data is loaded)
  if (requiredPermission && rbacData && !hasPermission(requiredPermission)) {
    return <Navigate 
      to="/unauthorized" 
      replace 
      state={{ from: window.location.pathname, requiredPermission }}
    />;
  }
  
  // Check RBAC feature access if specified (only after RBAC data is loaded)
  if (requiredFeature && rbacData && !canAccess(requiredFeature)) {
    return <Navigate 
      to="/unauthorized" 
      replace 
      state={{ from: window.location.pathname, requiredFeature }}
    />;
  }
  
  // Check role permissions if specified
  if (requiredRoles.length > 0 && user && !requiredRoles.includes(user.role as UserRole)) {
    return <Navigate 
      to="/unauthorized" 
      replace 
      state={{ from: window.location.pathname, requiredRole: requiredRoles.join(',') }}
    />;
  }
  
  // Check tenant level permissions if specified
  if (requiredTenantLevels.length > 0 && user && !requiredTenantLevels.includes(user.tenant_level as TenantLevel)) {
    return <Navigate 
      to="/unauthorized" 
      replace 
      state={{ from: window.location.pathname, requiredTenantLevel: requiredTenantLevels.join(',') }}
    />;
  }
  
  // Render the protected route
  return <Outlet />;
};

// Public route that redirects to dashboard if already authenticated
export const PublicRoute: React.FC = () => {
  const { isAuthenticated, isInitializing } = useAuth();
  
  // Show loading state only while checking existing session (not during sign-in)
  if (isInitializing) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Redirect to dashboard if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Render the public route
  return <Outlet />;
}; 